import {useMemo} from 'react';

import {type XAxisFormatValues} from '../../../../../generated/graphql';
import {type RunHistoryKeyInfoKeys} from '../../../../../types/run';
import {
  useFullScreenLinePlotMaxRunsDynamicConfig,
  useLinePlotMaxRunsDynamicConfig,
} from '../../../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {
  useRampFlagFullFidelityRunsPerChart,
  useRampFlagIncreaseFullScreenMaxRuns,
} from '../../../../../util/rampFeatureFlags/linePlotFeatureFlags';
import {useKeyInfoQueryContext} from '../../../../MultiRunWorkspace/KeyInfoQueryContext';
import {useIsFullScreenPanel} from '../../../../Panel/hooks/useIsFullScreenPanel';
import {type OrganizedSettings} from '../../../../PanelBank/types';
import {type RunsLinePlotConfig} from '../../../../PanelRunsLinePlot/types';
import {type Complete, type LinePlotSettings} from '../../types';
import {
  DEFAULT_X_AXIS_SETTINGS,
  getLinePlotSettingWithDefault,
} from '../linePlotDefaults';
import {getCascadingExcludeOutlierSetting} from './getCascadingExcludeOutliers';
import {getCascadingMaxRuns} from './getCascadingMaxRuns';
import {
  getDerivedPointVisualizationOption,
  useCascadingPointAggregationMethod,
} from './getCascadingPointAggregation';
import {getCascadingShowLegend} from './getCascadingShowLegend';

/**
 * We want to make sure the setting fields exist, but still allow them to be undefined.
 * When setting value is null, it indicates that a user hasn't modified the setting
 * yet. A few values are handled differently at workspace and section level, so those
 * individual hooks should handle those there.
 */
const getLinePlotSettings = (
  linePlotSettings: LinePlotSettings | undefined,
  historyKeys: RunHistoryKeyInfoKeys
): Complete<
  Omit<LinePlotSettings, 'pointVisualizationMethod' | 'excludeOutliers'>
> => {
  const sanitizedXAxis = getSanitizedXAxis(
    linePlotSettings?.xAxis,
    historyKeys
  );

  return {
    colorRunNames: linePlotSettings?.colorRunNames,
    coordinateZooming: linePlotSettings?.coordinateZooming,
    displayFullRunName: linePlotSettings?.displayFullRunName,
    fullFidelityMode: linePlotSettings?.fullFidelityMode,
    groupAgg: linePlotSettings?.groupAgg,
    groupingDepth: linePlotSettings?.groupingDepth,
    highlightedCompanionRunOnly: linePlotSettings?.highlightedCompanionRunOnly,
    limit: linePlotSettings?.limit,
    maxRuns: linePlotSettings?.maxRuns,
    showLegend: getCascadingShowLegend({
      // @ts-expect-error - suppressLegends is deprecated
      suppressLegends: linePlotSettings?.suppressLegends,
      showLegend: linePlotSettings?.showLegend,
    }),
    showXRange: linePlotSettings?.showXRange,
    smoothingType: linePlotSettings?.smoothingType,
    smoothingWeight: linePlotSettings?.smoothingWeight,
    tooltipNumberOfRuns: linePlotSettings?.tooltipNumberOfRuns,
    useRunsTableGroupingInPanels:
      linePlotSettings?.useRunsTableGroupingInPanels,
    xAxis: sanitizedXAxis,
    xAxisFormat: linePlotSettings?.xAxisFormat,
    xAxisMax: linePlotSettings?.xAxisMax,
    xAxisMin: linePlotSettings?.xAxisMin,
    xLogScale: linePlotSettings?.xLogScale,
    yAxisMax: linePlotSettings?.yAxisMax,
    yAxisMin: linePlotSettings?.yAxisMin,
    yLogScale: linePlotSettings?.yLogScale,
    showOriginalAfterSmoothing: linePlotSettings?.showOriginalAfterSmoothing,
    windowing: linePlotSettings?.windowing,
  };
};

const getSanitizedXAxis = (
  xAxis: string | undefined,
  historyKeys: RunHistoryKeyInfoKeys
) => {
  if (xAxis == null) {
    return undefined;
  }

  // Also known as "Relative Time (Wall)". This is a special case where we don't want to check against historyKeys
  // because it will never exist in there. The points for this xAxis are derived from "_runtime" and "_timestamp".
  // See https://github.com/wandb/core/blob/master/frontends/app/src/components/PanelRunsLinePlot/timePlots/readme.md for more details.
  // See "usePostProcessedData" that's used in "PanelDataContext" on how those points are created.
  if (xAxis === '_absolute_runtime') {
    return xAxis;
  }

  // If the xAxis value is not in historyKeys, then it's an invalid value and we'll default
  // to w&b _step instead
  return historyKeys[xAxis] == null ? DEFAULT_X_AXIS_SETTINGS.xAxis : xAxis;
};

// All fields in the type should exist, but can be undefined
export const useWorkspaceLinePlotSettings = (
  organizedSettings?: OrganizedSettings
): Complete<LinePlotSettings> => {
  const linePlotSettings = organizedSettings?.linePlot;

  // This feature is still under development
  // Note: once feature is fully released on prod, we can remove this hook
  const {pointVisualizationMethod} =
    useCascadingPointAggregationMethod(linePlotSettings);

  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();

  return useMemo(
    () => ({
      ...getLinePlotSettings(linePlotSettings, historyKeyInfo.keys),
      pointVisualizationMethod,
      excludeOutliers: getCascadingExcludeOutlierSetting(
        pointVisualizationMethod,
        linePlotSettings
      ),
    }),
    [linePlotSettings, pointVisualizationMethod, historyKeyInfo.keys]
  );
};

// All fields in the type should exist, but can be undefined
export const useSectionLinePlotSettings = (
  organizedSettings?: OrganizedSettings
): Complete<LinePlotSettings> => {
  const linePlotSettings = organizedSettings?.linePlot;
  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();

  return useMemo(
    () => ({
      ...getLinePlotSettings(linePlotSettings, historyKeyInfo.keys),
      pointVisualizationMethod: linePlotSettings?.pointVisualizationMethod,
      // We don't need to worry about the deprecated excludeOutliers fields because
      // section settings came after those changes
      excludeOutliers: linePlotSettings?.excludeOutliers,
    }),
    [linePlotSettings, historyKeyInfo.keys]
  );
};

/**
 * A hook version of getLinePlotSettingsWithDefaults, can be used where hooks are used to automatically get the proper override value based on the ramp flag.
 */
export const useLinePlotSettingsWithDefaults = (
  settings: LinePlotSettings | RunsLinePlotConfig | undefined
) => {
  const canOverrideMaxRuns = useRampFlagFullFidelityRunsPerChart();
  const canIncreaseFullScreenMaxRuns = useRampFlagIncreaseFullScreenMaxRuns();
  const dynamicConfigMaxRuns = useLinePlotMaxRunsDynamicConfig();
  const dynamicConfigFullScreenMaxRuns =
    useFullScreenLinePlotMaxRunsDynamicConfig();
  const {isFullScreen} = useIsFullScreenPanel();

  return useMemo(() => {
    return getLinePlotSettingsWithDefaults({
      settings,
      overrides: {
        canOverrideMaxRuns,
        isFullScreen,
        canIncreaseFullScreenMaxRuns,
        dynamicConfigMaxRuns,
        dynamicConfigFullScreenMaxRuns,
      },
    });
  }, [
    canIncreaseFullScreenMaxRuns,
    canOverrideMaxRuns,
    dynamicConfigFullScreenMaxRuns,
    dynamicConfigMaxRuns,
    isFullScreen,
    settings,
  ]);
};

/**
 * @param settingsType indicates what level the settings are
 *
 * This is used at the panel/most specific level. It will take the current value or the default value.
 * We want to resolve at the panel level, so that we can easily add or remove settings
 * at both the workspace and section levels.
 */
export const getLinePlotSettingsWithDefaults = ({
  settings,
  overrides = {},
}: {
  settings: LinePlotSettings | RunsLinePlotConfig | undefined;
  overrides?: {
    canOverrideMaxRuns?: boolean;
    isFullScreen?: boolean;
    canIncreaseFullScreenMaxRuns?: boolean;
    dynamicConfigMaxRuns?: number;
    dynamicConfigFullScreenMaxRuns?: number;
  };
}) => {
  const pointVisualizationMethod = getDerivedPointVisualizationOption(
    settings?.pointVisualizationMethod
  );

  const limit = getCascadingMaxRuns({
    canOverrideMaxRuns: overrides.canOverrideMaxRuns ?? false,
    pointVisualizationMethod,
    maxRuns:
      settings != null && 'maxRuns' in settings ? settings?.maxRuns : undefined,
    limit:
      settings != null && 'limit' in settings ? settings?.limit : undefined,
    isFullScreen: overrides.isFullScreen,
    canIncreaseFullScreenMaxRuns: overrides.canIncreaseFullScreenMaxRuns,
    dynamicConfigMaxRuns: overrides.dynamicConfigMaxRuns,
    dynamicConfigFullScreenMaxRuns: overrides.dynamicConfigFullScreenMaxRuns,
  });

  const xAxisFormat = getLinePlotSettingWithDefault(
    settings?.xAxisFormat,
    'xAxisFormat'
  );

  return {
    colorRunNames: getLinePlotSettingWithDefault(
      settings?.colorRunNames,
      'colorRunNames'
    ),
    coordinateZooming: getLinePlotSettingWithDefault(
      settings?.coordinateZooming,
      'coordinateZooming'
    ),
    displayFullRunName: getLinePlotSettingWithDefault(
      settings?.displayFullRunName,
      'displayFullRunName'
    ),
    excludeOutliers: settings?.excludeOutliers,
    fullFidelityMode: settings?.fullFidelityMode,
    groupAgg: getLinePlotSettingWithDefault(settings?.groupAgg, 'groupAgg'),
    groupingDepth:
      settings != null && 'groupingDepth' in settings
        ? settings.groupingDepth
        : undefined,
    highlightedCompanionRunOnly: getLinePlotSettingWithDefault(
      settings?.highlightedCompanionRunOnly,
      'highlightedCompanionRunOnly'
    ),
    limit: getLinePlotSettingWithDefault(limit, 'limit'),
    pointVisualizationMethod,
    showLegend: getLinePlotSettingWithDefault(
      settings?.showLegend,
      'showLegend'
    ),
    showXRange: getLinePlotSettingWithDefault(
      settings?.showXRange,
      'showXRange'
    ),
    smoothingType: getLinePlotSettingWithDefault(
      settings?.smoothingType,
      'smoothingType'
    ),
    smoothingWeight: getLinePlotSettingWithDefault(
      settings?.smoothingWeight,
      'smoothingWeight'
    ),
    showOriginalAfterSmoothing: getLinePlotSettingWithDefault(
      settings?.showOriginalAfterSmoothing,
      'showOriginalAfterSmoothing'
    ),
    tooltipNumberOfRuns: getLinePlotSettingWithDefault(
      settings?.tooltipNumberOfRuns,
      'tooltipNumberOfRuns'
    ),
    useRunsTableGroupingInPanels: getLinePlotSettingWithDefault(
      settings?.useRunsTableGroupingInPanels,
      'useRunsTableGroupingInPanels'
    ),
    xAxis: getLinePlotSettingWithDefault(settings?.xAxis, 'xAxis'),
    xAxisMax: settings?.xAxisMax ?? undefined,
    xAxisMin: settings?.xAxisMin ?? undefined,
    xAxisFormat: xAxisFormat as XAxisFormatValues | undefined,
    xLogScale: getLinePlotSettingWithDefault(settings?.xLogScale, 'xLogScale'),
    yAxisMax: settings?.yAxisMax ?? undefined,
    yAxisMin: settings?.yAxisMin ?? undefined,
    yLogScale: getLinePlotSettingWithDefault(settings?.yLogScale, 'yLogScale'),
  };
};
